import { Box, Theme } from '@mui/material';

import { LoadingSpinner, WorkflowMessage } from '@verantos-dev/shared-components';
import {
  CohortCharacteristicsResultType,
  CohortDefinitionStatus,
  CohortSummaryDetails,
  CohortSummaryAttributeResult,
  type DomainType,
} from '@verantos-dev/types-cohorts';

import { AgeAtIndexDistributionChart } from '../components/AgeAtIndexDistributionChart';
import { ChartLayout } from '../components/ChartLayout';
import { ClinicalCharacteristicsChart } from '../components/ClinicalCharacteristicsChart';
import { PieChart } from '../components/PieChart';
import { sanitizeAgeAtIndexData, sanitizeGenderData } from '../helpers';

interface CohortCharacteristicsContentProps {
  theme: Theme;
  latestCohortStatus: CohortDefinitionStatus | undefined;
  isCohortSummaryInitiallyLoading: boolean;
  cohortSummaryData: CohortSummaryDetails | undefined;
  hasSummaryData: boolean;
  cohortMessages: Record<string, string>;
  clinicalCharacteristicsDomainType?: DomainType;
  customChartTitle?: string;
  customDisplayData?: CohortSummaryAttributeResult[];
  isLoadingCustomData?: boolean;
}

export const CohortCharacteristicsContent = ({
  theme,
  latestCohortStatus,
  isCohortSummaryInitiallyLoading,
  hasSummaryData,
  cohortSummaryData,
  cohortMessages,
  clinicalCharacteristicsDomainType,
  customChartTitle,
  customDisplayData,
  isLoadingCustomData = false,
}: CohortCharacteristicsContentProps) => {
  if (
    isCohortSummaryInitiallyLoading ||
    (isLoadingCustomData && clinicalCharacteristicsDomainType)
  ) {
    return (
      <Box
        data-testid={'cohortCharacteristicsLoading'}
        sx={{
          borderRadius: '4px',
          boxShadow:
            '0px 1px 8px 0px rgba(0, 0, 0, 0.12), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 3px 3px -2px rgba(0, 0, 0, 0.20)',
          padding: theme.spacing(6),
          width: '100%',
          height: '348px',
        }}
      >
        <LoadingSpinner
          id="characteristicsLoadingSpinner"
          data-testid="characteristicsLoadingSpinner"
          title={
            isLoadingCustomData && clinicalCharacteristicsDomainType
              ? cohortMessages.loadingCustomSelectionTitle
              : cohortMessages.loadingTitle
          }
        />
      </Box>
    );
  }

  if (latestCohortStatus === CohortDefinitionStatus.DRAFT) {
    return (
      <WorkflowMessage
        id={'CohortCharacteristics-draft'}
        title={cohortMessages.draftTitle}
        message={cohortMessages.draftMessage}
        sx={{ width: '100%', height: '348px' }}
      />
    );
  }

  if (
    latestCohortStatus === CohortDefinitionStatus.PROCESSING &&
    (!cohortSummaryData ||
      (!cohortSummaryData.ageAtIndex &&
        !cohortSummaryData.gender &&
        !cohortSummaryData.ethnicity &&
        !cohortSummaryData.race))
  ) {
    return (
      <WorkflowMessage
        id={'CohortCharacteristics-processing'}
        title={cohortMessages.processingTitle}
        message={cohortMessages.processingMessage}
        sx={{ width: '100%', height: '348px' }}
      />
    );
  }

  const ethnicityData = cohortSummaryData?.[CohortCharacteristicsResultType.ETHNICITY];
  const genderData = sanitizeGenderData(
    cohortSummaryData?.[CohortCharacteristicsResultType.GENDER]
  );
  const raceData = cohortSummaryData?.[CohortCharacteristicsResultType.RACE];
  const ageAtIndexData = sanitizeAgeAtIndexData(
    cohortSummaryData?.[CohortCharacteristicsResultType.AGE_AT_INDEX]
  );

  const clinicalCharacteristicsData =
    customDisplayData || cohortSummaryData?.[clinicalCharacteristicsDomainType as DomainType];

  if (!hasSummaryData) {
    return (
      <WorkflowMessage
        id={'CohortCharacteristics-processed-no-results'}
        title={cohortMessages.errorTitle}
        message={cohortMessages.errorMessage}
        sx={{ width: '100%', height: '348px' }}
      />
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {clinicalCharacteristicsDomainType ? (
        <ChartLayout
          chartType={clinicalCharacteristicsDomainType as CohortCharacteristicsResultType}
          heading={customChartTitle || 'Clinical characteristics'}
          isEmpty={!clinicalCharacteristicsData?.length}
          skipHeight
          sx={{ mb: theme.spacing(6) }}
        >
          <ClinicalCharacteristicsChart
            data={clinicalCharacteristicsData}
            resultType={clinicalCharacteristicsDomainType as CohortCharacteristicsResultType}
          />
        </ChartLayout>
      ) : (
        <>
          <ChartLayout
            chartType={CohortCharacteristicsResultType.AGE_AT_INDEX}
            heading="Age at index"
            isEmpty={!ageAtIndexData?.length}
            skipHeight
            sx={{ mb: theme.spacing(6) }}
          >
            <AgeAtIndexDistributionChart data={ageAtIndexData} />
          </ChartLayout>

          {/* Each donut card grows equally but wraps to fewer-per-row as
              the column is squeezed (e.g. the Research Assistant sidebar
              open beside the Results tab). The `flex: 1 1 360px` basis
              gives each responsive-width chart a concrete width to fill —
              without it a responsive chart's natural width is 0 and the
              card would collapse in the flex row — and the 360px floor
              keeps the donut + its legend from being squeezed so small
              the legend overlaps the chart. */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'stretch',
              gap: theme.spacing(6),
            }}
          >
            <ChartLayout
              chartType={CohortCharacteristicsResultType.GENDER}
              heading="Gender"
              isEmpty={!genderData?.length}
              sx={{ flex: '1 1 360px', minWidth: 360 }}
            >
              <PieChart data={genderData} resultType={CohortCharacteristicsResultType.GENDER} />
            </ChartLayout>

            <ChartLayout
              chartType={CohortCharacteristicsResultType.RACE}
              heading="Race"
              isEmpty={!raceData?.length}
              sx={{ flex: '1 1 360px', minWidth: 360 }}
            >
              <PieChart data={raceData} resultType={CohortCharacteristicsResultType.RACE} />
            </ChartLayout>

            <ChartLayout
              chartType={CohortCharacteristicsResultType.ETHNICITY}
              heading="Ethnicity"
              isEmpty={!ethnicityData?.length}
              sx={{ flex: '1 1 360px', minWidth: 360 }}
            >
              <PieChart
                data={ethnicityData}
                resultType={CohortCharacteristicsResultType.ETHNICITY}
              />
            </ChartLayout>
          </Box>
        </>
      )}
    </Box>
  );
};
